/**
 * Routing and display rules for the Research Dialogue Library (guide §13).
 */

const PIN_KEYS = ["dialogue_id", "foundation_version", "library_version"];

/**
 * The in-app route for an Overview's `view_the_research` block. The block
 * carries the pinned ids as fields; its `href` is an API path, so it is only
 * read as a fallback source for those ids, never navigated to.
 */
export const researchRoute = (view) => {
  if (!view) return null;
  let fromHref = {};
  if (view.href) {
    try {
      const url = new URL(view.href, "https://aris.invalid");
      const last = url.pathname.split("/").filter(Boolean).pop();
      fromHref = { research_dialogue_id: last };
      PIN_KEYS.forEach((k) => {
        const v = url.searchParams.get(k);
        if (v) fromHref[k] = v;
      });
    } catch (_) {
      fromHref = {};
    }
  }
  const id = view.research_dialogue_id || fromHref.research_dialogue_id;
  if (!id) return null;
  const qs = new URLSearchParams();
  PIN_KEYS.forEach((k) => {
    const v = view[k] ?? fromHref[k];
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  const q = qs.toString();
  return `/explore/research/${encodeURIComponent(id)}${q ? `?${q}` : ""}`;
};

/** The pinned query params, read back off the research route. */
export const pinFromSearch = (searchParams) => {
  const out = {};
  PIN_KEYS.forEach((k) => {
    const v = searchParams.get(k);
    if (v) out[k] = v;
  });
  return out;
};

/** Any string the library marks as unfinished work (§13.3) never reaches a member. */
export const isPlaceholder = (text) =>
  typeof text === "string" && text.trim().startsWith("⚠ PLACEHOLDER");

/** Drop empty and placeholder text; `null` when nothing member-safe is left. */
export const safeText = (text) =>
  typeof text === "string" && text.trim() && !isPlaceholder(text) ? text : null;

/** A source's year: `year ?? year_label` — "Current" is a real value (§13.1 rule 2). */
export const sourceYear = (source) => {
  const y = source?.year ?? source?.year_label;
  return y === null || y === undefined || y === "" ? null : String(y);
};

const EVIDENCE_LABEL = {
  guideline: "Authoritative guidance",
  guidance: "Authoritative guidance",
  review: "Review",
  systematic_review: "Systematic review",
  meta_analysis: "Meta-analysis",
  peer_reviewed: "Peer-reviewed research",
  study: "Research study",
};

/** `evidence_type` is an enum-ish slug; it is never printed raw. */
export const evidenceLabel = (type) => {
  if (!type) return null;
  const key = String(type).toLowerCase();
  if (EVIDENCE_LABEL[key]) return EVIDENCE_LABEL[key];
  const words = key.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : null;
};

/**
 * A finding's `icon` is a library key with no fixed vocabulary, so it is
 * matched loosely onto the design's four glyphs, with a neutral default.
 */
export const findingIconKind = (icon) => {
  const k = String(icon || "").toLowerCase();
  if (/(user|people|person|family|relation|social|group)/.test(k)) return "users";
  if (/(sleep|moon|rest|night)/.test(k)) return "sleep";
  if (/(leaf|environment|nature|plant|world|home)/.test(k)) return "leaf";
  if (/(heart|emotion|feel|mood|love)/.test(k)) return "heart";
  return "neutral";
};

/** A source card's lead line: the institution, else the authors (v26.18 keeps them apart). */
export const sourceByline = (source) =>
  safeText(source?.institution) ||
  (Array.isArray(source?.authors) ? source.authors.filter(safeText).join(", ") : "");
